import { z } from "zod";
import type {
    Promotion,
    PromotionRequest,
    PromotionSlotType,
} from "@models/promotion";
import {
    lastDayToApi,
    lastDayToInput,
    startToApi,
    startToInput,
} from "./promotionDates";

// DomainConstraint.Promotion on the API.
const LABEL_MAX = 128;
const CTA_LABEL_MAX = 64;
const CTA_HREF_MAX = 2048;

// The API's PromotionCtaHref rule (no outside links, PO ruling 2026-10-05): a
// path on this site starting with a single "/". Whitespace, control
// characters and backslashes are refused anywhere, because a browser strips
// tabs and newlines from a URL and reads "\" as "/", so "/\evil.com" leaves
// the site exactly as "//evil.com" does. Empty passes: the link is optional.
const isAppPath = (href: string): boolean => {
    if (href === "") {
        return true;
    }
    const hasRefusedCharacter = Array.from(href).some((character) => {
        const code = character.charCodeAt(0);
        return (
            /\s/.test(character) ||
            code < 0x20 ||
            (code >= 0x7f && code <= 0x9f) ||
            character === "\\"
        );
    });
    return !hasRefusedCharacter && href.startsWith("/") && !href.startsWith("//");
};

// The date inputs' bounds (review F5). A typo can leave a year like 0026
// (Date.UTC reads two-digit years as 19xx) or one of five digits, which the
// day arithmetic and the API's DateTime cannot carry; and the start/last-day
// compare is a string compare, so both must be four-digit years. The last
// day stops one day short of the end of 9999: it is sent as the next day.
export const DATE_MIN = "2000-01-01";
export const START_DATE_MAX = "9999-12-31";
export const LAST_DAY_MAX = "9999-12-30";
const YEAR_MESSAGE = "Enter a year between 2000 and 9999";

const hasYearInRange = (value: string): boolean => {
    const match = /^(\d+)-\d{2}-\d{2}$/.exec(value);
    if (!match || match[1].length !== 4) {
        return false;
    }
    return Number(match[1]) >= 2000;
};

const dateField = () =>
    z.string().refine((value) => value === "" || hasYearInRange(value), YEAR_MESSAGE);

// Mirrors PromotionCommandValidator, so the admin is not taught the rules by a
// 400. The vendor and the image are checked elsewhere: the vendor is a picker
// of the API's own list, and a refused file is never taken (productImageFile).
export const promotionSchema = z
    .object({
        slot: z.string().min(1, "Choose a slot"),
        label: z
            .string()
            .refine((value) => value.trim() !== "", "Label is required")
            .refine(
                (value) => value.trim().length <= LABEL_MAX,
                `Label must be ${LABEL_MAX} characters or fewer`,
            ),
        ctaLabel: z
            .string()
            .refine(
                (value) => value.trim().length <= CTA_LABEL_MAX,
                `Button label must be ${CTA_LABEL_MAX} characters or fewer`,
            ),
        ctaHref: z
            .string()
            .refine(
                (value) => value.trim().length <= CTA_HREF_MAX,
                `Link must be ${CTA_HREF_MAX} characters or fewer`,
            )
            .refine(
                (value) => isAppPath(value.trim()),
                "Use a path on this site that starts with a single “/”, such as /c/browse/12. Links to other sites aren't allowed.",
            ),
        vendorId: z.string(),
        startDate: dateField(),
        lastDay: dateField().refine(
            (value) => value <= LAST_DAY_MAX || !hasYearInRange(value),
            "The last day can be 12/30/9999 at the latest",
        ),
        isActive: z.boolean(),
    })
    .superRefine((values, ctx) => {
        // A button with a label and nowhere to go. A link with no label is
        // fine: the storefront labels it "Shop now".
        if (values.ctaLabel.trim() !== "" && values.ctaHref.trim() === "") {
            ctx.addIssue({
                code: "custom",
                path: ["ctaHref"],
                message: "Add a link for this button label, or clear the label",
            });
        }

        // Both are yyyy-MM-dd with a four-digit year (a bad year is reported
        // on its own field instead), so a string compare is a date compare.
        // The same day is fine: the promotion shows for that one day.
        if (
            hasYearInRange(values.startDate) &&
            hasYearInRange(values.lastDay) &&
            values.startDate > values.lastDay
        ) {
            ctx.addIssue({
                code: "custom",
                path: ["lastDay"],
                message: "The last day can't be before the start date",
            });
        }
    });

export type PromotionFormValues = z.infer<typeof promotionSchema>;

// The form's starting values: empty and active for a new promotion, the
// stored row (its end shown as the last day) for an edit.
export const toFormValues = (
    promotion: Promotion | null,
): PromotionFormValues => ({
    slot: promotion ? String(promotion.slot) : "",
    label: promotion?.label ?? "",
    ctaLabel: promotion?.ctaLabel ?? "",
    ctaHref: promotion?.ctaHref ?? "",
    vendorId:
        promotion?.vendorId != null ? String(promotion.vendorId) : "",
    startDate: startToInput(promotion?.startsAt ?? null),
    lastDay: lastDayToInput(promotion?.endsAt ?? null),
    isActive: promotion?.isActive ?? true,
});

const orNull = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
};

// The form's values as the API request. `promotion` is the stored row of an
// edit, so a date the admin left alone goes back exactly as stored.
export const toPromotionRequest = (
    values: PromotionFormValues,
    promotion: Promotion | null,
    image: File | null,
): PromotionRequest => ({
    slot: Number(values.slot) as PromotionSlotType,
    label: values.label.trim(),
    ctaLabel: orNull(values.ctaLabel),
    ctaHref: orNull(values.ctaHref),
    startsAt: startToApi(values.startDate, promotion?.startsAt ?? null),
    endsAt: lastDayToApi(values.lastDay, promotion?.endsAt ?? null),
    vendorId: values.vendorId === "" ? null : Number(values.vendorId),
    image,
});
